// Device fingerprint generator for anti-abuse usage tracking.
// Produces a stable hash from browser signals that persists across
// localStorage clears and incognito sessions (same browser).

let cachedFingerprint: string | null = null;

function djb2(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('FRAME.fp', 2, 15);
    ctx.fillStyle = 'rgba(102,204,0,0.7)';
    ctx.fillText('FRAME.fp', 4, 17);
    return canvas.toDataURL();
  } catch {
    return '';
  }
}

function getWebGLFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl || !(gl instanceof WebGLRenderingContext)) return '';
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return gl.getParameter(gl.RENDERER) || '';
    return [
      gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || '',
      gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '',
    ].join('|');
  } catch {
    return '';
  }
}

function collectSignals(): string {
  const signals: string[] = [];

  // Screen
  signals.push(`${screen.width}x${screen.height}`);
  signals.push(`${screen.colorDepth}`);
  signals.push(`${window.devicePixelRatio}`);

  // Navigator
  signals.push(navigator.language || '');
  signals.push(`${navigator.hardwareConcurrency || 0}`);
  signals.push(navigator.platform || '');

  // Timezone
  try {
    signals.push(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    signals.push('');
  }

  // Canvas
  signals.push(getCanvasFingerprint());

  // WebGL
  signals.push(getWebGLFingerprint());

  return signals.join('||');
}

async function sha256(input: string): Promise<string> {
  try {
    const data = new TextEncoder().encode(input);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hash))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    // Fallback for non-HTTPS or unsupported environments
    return djb2(input) + djb2(input.split('').reverse().join(''));
  }
}

export async function getDeviceFingerprint(): Promise<string> {
  if (cachedFingerprint) return cachedFingerprint;
  const raw = collectSignals();
  cachedFingerprint = await sha256(raw);
  return cachedFingerprint;
}
